import { observer } from 'mobx-react-lite';
import { useContext, useState, type ReactNode } from 'react';

import { Icons } from '@/assets/icons/icons';
import { cn } from '@/lib/utils';

import { FeedDensityContext } from './feedDensity';
import { FeedIcon } from './FeedIcon';
import { FeedRowRoleContext } from './feedRowRole';
import { FEED_ROW_GRID, FEED_STRUCK_TITLE_CLASS } from './feedStyles';

import type { CardChildren } from '@littlebird/action-cards';
import type { ActionCard } from '@littlebird/sdk';

type FeedSubtaskListProps<TEntry extends { readonly card: ActionCard }> = {
  readonly subtasks: CardChildren<TEntry, ActionCard>;
  // A selected subtask must stay visible, including when opened by keyboard navigation
  readonly subtaskOpen: boolean;
  readonly onExpand: () => void;
  readonly onCollapse: () => void;
  readonly renderRow: (entry: TEntry) => ReactNode;
};

// The rail sits under the parent's icon, and the subtasks' icons line up with the parent's title
const COMFORTABLE_INDENT =
  'ml-[calc(40*var(--ac-u))] pl-[calc(16*var(--ac-u))] pr-[calc(16*var(--ac-u))]';
const INDENT = {
  expanded: COMFORTABLE_INDENT,
  comfortable: COMFORTABLE_INDENT,
  compact: 'ml-[calc(32*var(--ac-u))] pl-[calc(4*var(--ac-u))]',
} as const;

// The disclosure sits just inside the subtask rail
const LINE_CLASS =
  'py-1 pl-[calc(12*var(--ac-u))] text-[length:calc(12*var(--ac-u))]';

type Row<TEntry> =
  | { readonly kind: 'open'; readonly entry: TEntry }
  | { readonly kind: 'done'; readonly card: ActionCard };

const DoneRow = observer(function DoneRow({
  card,
}: {
  readonly card: ActionCard;
}) {
  return (
    <div data-testid="card-child-done" className={cn(FEED_ROW_GRID, 'min-h-0')}>
      <FeedIcon failed={false}>
        <span className="flex size-[var(--ac-row-tile)] items-center justify-center text-foreground-muted">
          <Icons.check
            aria-hidden="true"
            className="size-[calc(14*var(--ac-u))]"
          />
        </span>
      </FeedIcon>
      <span
        className={cn(
          'min-w-0 truncate text-[length:calc(13*var(--ac-u))]',
          FEED_STRUCK_TITLE_CLASS
        )}
      >
        {card.title || 'Done'}
      </span>
      <span />
    </div>
  );
});

export const FeedSubtaskList = <TEntry extends { readonly card: ActionCard }>({
  subtasks,
  subtaskOpen,
  onExpand,
  onCollapse,
  renderRow,
}: FeedSubtaskListProps<TEntry>) => {
  const density = useContext(FeedDensityContext);
  const [allAsked, setAllAsked] = useState(false);
  const { current } = subtasks;
  const currentIds = new Set(current.steps.map(({ card }) => card.id));
  // The subtasks still to do come first, then the done ones
  const toOpenRow = (entry: TEntry): Row<TEntry> => ({ kind: 'open', entry });
  const listed: Row<TEntry>[] = [
    ...subtasks.open.map(toOpenRow),
    ...subtasks.done.map((card) => ({ kind: 'done', card }) as const),
  ];
  const allShown = allAsked || subtaskOpen;
  if (listed.length === 0) return null;

  return (
    <FeedDensityContext.Provider value="compact">
      <div
        data-testid="card-children"
        // Part of its parent, so a click here keeps the parent open
        data-feed-subtasks
        data-feed-density="compact"
        className={cn(
          'mb-2 flex flex-col border-l border-border',
          INDENT[density]
        )}
      >
        {allShown
          ? listed.map((row) =>
              row.kind === 'done' ? (
                <DoneRow key={row.card.id} card={row.card} />
              ) : (
                <div
                  key={row.entry.card.id}
                  data-testid={
                    currentIds.has(row.entry.card.id)
                      ? 'card-current-step'
                      : 'card-child'
                  }
                >
                  <FeedRowRoleContext.Provider
                    value={currentIds.has(row.entry.card.id) ? 'step' : 'card'}
                  >
                    {renderRow(row.entry)}
                  </FeedRowRoleContext.Provider>
                </div>
              )
            )
          : null}
        <button
          type="button"
          data-testid="card-show-subtasks"
          aria-expanded={allShown}
          className={cn(
            LINE_CLASS,
            'flex items-center gap-[calc(4*var(--ac-u))] self-start rounded text-foreground-subtle hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring'
          )}
          onClick={() => {
            setAllAsked(!allShown);
            if (allShown) onCollapse();
            else onExpand();
          }}
        >
          {allShown
            ? 'Show less'
            : listed.length === 1
              ? 'Show 1 subtask'
              : `Show all ${listed.length}`}
          <Icons.chevronDown
            aria-hidden="true"
            className={cn(
              'size-[calc(11*var(--ac-u))]',
              allShown && 'rotate-180'
            )}
          />
        </button>
      </div>
    </FeedDensityContext.Provider>
  );
};
